/**
 * Canonical URLs for the product filter surfaces (collection list, slab list,
 * advanced search).
 *
 * One filter state must map to exactly one URL, or the same results become
 * reachable under two addresses and the path/querystring split stops being a
 * crawl-budget win. The rule:
 *
 *   - exactly one active filter, on a dimension that has a semantic path here
 *     → the path (/prodotti/effetto/marmo/)
 *   - anything else → the base URL plus a querystring in a stable order
 *     (/prodotti/?color=beige&effect=marmo)
 *
 * So adding a second filter moves the first one out of the path. That jump is
 * deliberate: keeping the entry path and appending refinements would give
 * /effetto/marmo/?color=beige and /prodotti/?effect=marmo&color=beige as two
 * spellings of one state, depending only on the order the user clicked.
 *
 * Which dimensions have a semantic path is decided server-side and injected
 * (products.views._semantic_filter_urls): the segments are translated, and the
 * legal set is cluster-dependent (no brand path off the master cluster). The
 * client never derives them — it would drift from the routes that 404.
 */

/** Placeholder the server reverses the semantic routes with. */
export const VALUE_SENTINEL = '__value__';

/**
 * Querystring params that are not filters. `page` rides the same querystring
 * (the API paginates on it), and without this it would be read back as a
 * filter, forwarded to the API as one, and re-emitted into every URL we write.
 */
const NON_FILTER_PARAMS = new Set(['page']);

export interface FilterUrlConfig {
  /** Unfiltered products home, e.g. `/it/abk/prodotti/`. */
  baseUrl: string;
  /** `{filter_name: url_template}` for the dimensions with a semantic path. */
  semanticPaths: Record<string, string>;
}

export type SelectedFilters = Record<string, string>;

function activeEntries(selected: SelectedFilters): [string, string][] {
  return Object.entries(selected || {}).filter(
    ([, value]) => value !== '' && value !== null && value !== undefined,
  );
}

/** The one URL that represents `selected` on this surface. */
export function canonicalUrl(
  selected: SelectedFilters,
  config: FilterUrlConfig,
): string {
  const entries = activeEntries(selected);

  if (entries.length === 0) return config.baseUrl;

  if (entries.length === 1) {
    const [dimension, value] = entries[0];
    const template = config.semanticPaths?.[dimension];
    if (template) {
      return template.replace(VALUE_SENTINEL, encodeURIComponent(String(value)));
    }
  }

  // Alphabetical by filter name: any stable order works, this one needs no
  // config and cannot drift from the facet order the API happens to return.
  const params = new URLSearchParams();
  entries
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .forEach(([key, value]) => params.set(key, String(value)));

  return `${config.baseUrl}?${params.toString()}`;
}

/** Pull the value out of `pathname` if it matches a semantic template. */
function matchSemanticPath(template: string, pathname: string): string | null {
  const sentinel = template.indexOf(VALUE_SENTINEL);
  if (sentinel === -1) return null;

  const prefix = template.slice(0, sentinel);
  const suffix = template.slice(sentinel + VALUE_SENTINEL.length);

  if (!pathname.startsWith(prefix) || !pathname.endsWith(suffix)) return null;

  const value = pathname.slice(prefix.length, pathname.length - suffix.length);
  if (!value || value.includes('/')) return null;

  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
}

/**
 * Read the filter state back out of a URL — the inverse of `canonicalUrl`,
 * used on popstate, where the server-rendered initial payload is stale.
 *
 * A matching semantic path wins outright: it is a single-filter state by
 * construction, so any querystring alongside it is not something we wrote.
 */
export function filtersFromLocation(
  config: FilterUrlConfig,
  location: { pathname: string; search: string },
): SelectedFilters {
  for (const [dimension, template] of Object.entries(config.semanticPaths || {})) {
    const value = matchSemanticPath(template, location.pathname);
    if (value) return { [dimension]: value };
  }

  return filtersFromQuery(location.search);
}

/**
 * The filters a querystring carries, `page` excluded. For surfaces that merge
 * the querystring over a server-injected semantic filter instead of reading
 * the whole location back.
 */
export function filtersFromQuery(search: string): SelectedFilters {
  const filters: SelectedFilters = {};
  for (const [key, value] of new URLSearchParams(search)) {
    if (value && !NON_FILTER_PARAMS.has(key)) filters[key] = value;
  }
  return filters;
}

/** The page a querystring asks for; 1 unless it is a positive integer. */
export function pageFromSearch(search: string): number {
  const raw = new URLSearchParams(search).get('page') || '';
  return /^[1-9]\d*$/.test(raw) ? Number(raw) : 1;
}

/**
 * The URL of `page` for the filter state `selected`: the canonical URL, plus
 * `page` from page 2 on. It matches the page's own canonical, since core.seo
 * keeps `page` there (CANONICAL_QUERY_PARAMS), so a link written with it
 * points at an address that declares itself canonical.
 */
export function pageUrl(
  selected: SelectedFilters,
  config: FilterUrlConfig,
  page: number,
): string {
  const url = canonicalUrl(selected, config);
  if (page <= 1) return url;
  return `${url}${url.includes('?') ? '&' : '?'}page=${page}`;
}
